import { Check, ChevronDown, Languages } from 'lucide-react';
import { LANGUAGE_NAMES, LANGUAGE_SHORT, LOCALES, locale, strings, switchLocale, type Locale } from './strings.ts';
import { cn } from './ui/cn.ts';
import { UiMenu, UiMenuContent, UiMenuItem, UiMenuTrigger } from './ui/dropdown-menu.tsx';

/** A language's name in its own words and script: Chinese is drawn in the system's face, so a page in another language downloads none of Noto Sans SC (sliced by unicode-range, kit.css). */
function OwnWords({ of, words }: { readonly of: Locale; readonly words: string }) {
  return of === 'zh' ? (
    <span lang="zh-Hans" className="font-system">
      {words}
    </span>
  ) : (
    <span lang={of}>{words}</span>
  );
}

/**
 * The language menu, in every page's top bar or header: a bordered button
 * showing the language in force ("EN", "中文", "BM"), which opens a list of
 * every language in its own words, the one in force ticked. Picking another
 * saves the choice on the device and reloads the page in it. `onBand`:
 * white, on the brand's red band: a pill with the language alone.
 */
export function LanguageMenu({ onBand = false }: { readonly onBand?: boolean }) {
  const now = locale();
  return (
    <UiMenu>
      <UiMenuTrigger
        aria-label={strings.shell.switchLanguage}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-s1 border text-caption font-semibold whitespace-nowrap transition-colors select-none focus-visible:outline-2 focus-visible:outline-offset-2 print:hidden [&_svg]:size-s4 [&_svg]:shrink-0',
          onBand ? 'h-[2.25rem] rounded-chip border-on-brand/70 px-s3 text-on-brand before:absolute before:-inset-s1 hover:bg-on-brand/15 focus-visible:outline-on-brand' : 'h-s6 rounded-control border-line-strong bg-surface px-s2 text-ink before:absolute before:-inset-s1 hover:bg-sunk focus-visible:outline-brand',
        )}
      >
        {!onBand && <Languages aria-hidden="true" />}
        <OwnWords of={now} words={LANGUAGE_SHORT[now]} />
        <ChevronDown aria-hidden="true" />
      </UiMenuTrigger>
      <UiMenuContent className="min-w-[12rem]">
        {LOCALES.map((one) => (
          <UiMenuItem
            key={one}
            aria-current={one === now || undefined}
            onSelect={() => {
              if (one !== now) switchLocale(one);
            }}
          >
            <span className="flex-1">
              <OwnWords of={one} words={LANGUAGE_NAMES[one]} />
            </span>
            {one === now && <Check aria-hidden="true" />}
          </UiMenuItem>
        ))}
      </UiMenuContent>
    </UiMenu>
  );
}
